import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { TabType, UserProfile, MediaHistoryItem, AppUser } from './types';
import { initialUserProfile, initialMediaHistory } from './mockData';
import { MobileApp } from './components/MobileApp';
import { WebView } from './components/WebView';
import { CompleteProfileForm } from './features/auth/CompleteProfileForm';
import {
  getSavedSession,
  saveLocalSession,
  logoutUser,
  handleAuthRedirect,
  syncUserProfileToFirestore,
  ensureUserProfileDocuments,
  recordLastLoginAt,
  getUserProfileFromFirestore,
  fallbackUsername,
  isUserAdmin,
  isAdminRole,
  isSuperAdmin
} from './backend/authService';
import {
  subscribeToUserMediaHistory,
  deleteMediaItemFromDb
} from './backend/mediaService';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './backend/firebase';
import { refreshSecurityToken } from './backend/securityClaims';
import { Capacitor } from '@capacitor/core';
import { setPresence } from './backend/realtimeService';
import { seedOfficialFollowsForUser } from './backend/officialFollowService';
import { Check } from 'lucide-react';
import { DEFAULT_POLICIES, getPolicies, hasCurrentPolicyConsent, seedDefaultPoliciesForSuperAdmin } from './backend/policyService';
import { PolicyDocument } from './types';

import { SuspensionScreen } from './features/shared/components/SuspensionScreen';
import { markProfilePromptSkipped, shouldShowProfilePrompt } from './utils/profilePrompt';
import { FeedbackPage } from './features/feedback/FeedbackPage';
import { AppSupportPage } from './features/app-support/AppSupportPage';

const AuthModal = lazy(() => import('./features/auth/AuthModal').then(m => ({ default: m.AuthModal })));
const AdminRouter = lazy(() => import('./features/admin/routing/AdminRouter').then(m => ({ default: m.AdminRouter })));
const OfficialDashboard = lazy(() => import('./features/official/OfficialDashboard').then(m => ({ default: m.OfficialDashboard })));
const PoliciesPage = lazy(() => import('./features/policies/PoliciesPage').then(m => ({ default: m.PoliciesPage })));
const DeleteAccountPage = lazy(() => import('./features/policies/DeleteAccountPage').then(m => ({ default: m.DeleteAccountPage })));
const AboutPage = lazy(() => import('./pages/public/About').then(m => ({ default: m.AboutPage })));
const FeaturesPage = lazy(() => import('./pages/public/Features').then(m => ({ default: m.FeaturesPage })));
const FaqPage = lazy(() => import('./pages/public/Faq').then(m => ({ default: m.FaqPage })));
const ContactPage = lazy(() => import('./pages/public/Contact').then(m => ({ default: m.ContactPage })));
const PrivacyPage = lazy(() => import('./pages/public/Privacy').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/public/Terms').then(m => ({ default: m.TermsPage })));
const BlogListPage = lazy(() => import('./pages/public/blog/BlogList').then(m => ({ default: m.BlogListPage })));
const BlogPostPage = lazy(() => import('./pages/public/blog/BlogPost').then(m => ({ default: m.BlogPostPage })));
const AppPermissionsModal = lazy(() => import('./features/shared/components/AppPermissionsModal').then(m => ({ default: m.AppPermissionsModal })));

const POLICY_CONSENT_DELAY_MS = 48 * 60 * 60 * 1000;

const isPolicyConsentDue = (user: AppUser): boolean => {
  const firstJoinedAt = user.firstJoinedAt || user.createdAt;
  if (!firstJoinedAt) return false;
  const joinedAt = Date.parse(firstJoinedAt);
  return Number.isFinite(joinedAt) && Date.now() >= joinedAt + POLICY_CONSENT_DELAY_MS;
};

export default function App() {
  const isNativeApp = Capacitor.isNativePlatform();
  const [activeTab, setActiveTab] = useState<TabType>('home');
  const [userProfile, setUserProfile] = useState<UserProfile>(initialUserProfile);
  const [mediaHistory, setMediaHistory] = useState<MediaHistoryItem[]>(initialMediaHistory);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Authentication state
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(true);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [currentPath, setCurrentPath] = useState(() => window.location.pathname);
  const [showPermissionsModal, setShowPermissionsModal] = useState(() => {
    return localStorage.getItem('yetrykar_permissions_agreed') !== 'true';
  });
  const [policies, setPolicies] = useState<PolicyDocument[]>(() => Object.values(DEFAULT_POLICIES));

  // Mandatory Profile Form state
  const [showProfileForm, setShowProfileForm] = useState(false);
  const profilePromptCheckedUid = useRef<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  const showToast = (msg: string, durationMs = 3500) => {
    if (toastTimer.current !== undefined) window.clearTimeout(toastTimer.current);
    setToastMessage(msg);
    toastTimer.current = window.setTimeout(() => setToastMessage(null), durationMs);
  };

  const navigateToPath = (nextPath: string) => {
    window.history.pushState({}, '', nextPath);
    setCurrentPath(window.location.pathname);
  };

  useEffect(() => {
    void getPolicies().then(setPolicies);
  }, []);

  useEffect(() => {
    if (!currentUser || !isSuperAdmin(currentUser.email, currentUser.role)) return;
    void seedDefaultPoliciesForSuperAdmin().then(() => getPolicies().then(setPolicies)).catch(error => {
      console.warn('Could not seed default policy documents:', error);
    });
  }, [currentUser?.uid]);

  useEffect(() => {
    const handleRouteChange = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  useEffect(() => {
    const isAdminRoute = currentPath === '/admin' || currentPath.startsWith('/admin/');
    if (!isAdminRoute) return;

    const elements = [document.documentElement, document.body, document.getElementById('root')];
    const previousStyles = elements.map(element => element ? {
      element,
      height: element.style.height,
      overflow: element.style.overflow,
      overflowY: element.style.overflowY
    } : null);

    for (const element of elements) {
      if (!element) continue;
      element.style.height = 'auto';
      element.style.overflow = 'visible';
      element.style.overflowY = 'auto';
    }

    return () => {
      for (const previous of previousStyles) {
        if (!previous) continue;
        previous.element.style.height = previous.height;
        previous.element.style.overflow = previous.overflow;
        previous.element.style.overflowY = previous.overflowY;
      }
    };
  }, [currentPath]);

  useEffect(() => {
    const handleToast = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: unknown; durationMs?: unknown }>).detail;
      if (typeof detail?.message !== 'string') return;
      const durationMs = typeof detail.durationMs === 'number' ? detail.durationMs : 3500;
      showToast(detail.message, durationMs);
    };
    window.addEventListener('yetrykar:toast', handleToast);
    return () => window.removeEventListener('yetrykar:toast', handleToast);
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) return;
    return onSnapshot(doc(db, 'users', currentUser.uid), snapshot => {
      if (!snapshot.exists()) return;
      const suspension = snapshot.get('suspension');
      setCurrentUser(previous => previous?.uid === currentUser.uid
        ? { ...previous, suspension }
        : previous);
    }, error => {
      console.error('Could not follow account suspension status:', error);
    });
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!authReady || isAuthLoading || !currentUser?.uid) return;
    let cancelled = false;
    const user = currentUser;

    void (async () => {
      try {
        await ensureUserProfileDocuments(user);
      } catch (error) {
        console.error('Could not ensure authenticated profile documents:', error);
        return;
      }

      try {
        await recordLastLoginAt(user.uid);
      } catch (error) {
        console.error('Could not record this login:', error);
      }

      if (cancelled) return;
      try {
        await seedOfficialFollowsForUser(user, () => !cancelled && auth.currentUser?.uid === user.uid);
      } catch (error) {
        console.error('Could not seed official account follows:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authReady, isAuthLoading, currentUser?.uid]);

  // Sync Firebase Auth state & persistent session
  useEffect(() => {
    const deepLink = window.location.pathname.match(/^\/c\/(post|short|long|live)\/([^/]+)/);
    if (deepLink) {
      const tabForType: Record<string, TabType> = {
        post: 'home',
        short: 'shorts',
        long: 'longs',
        live: 'live'
      };
      setActiveTab(tabForType[deepLink[1]]);
    }
  }, []);

  useEffect(() => {
    const savedSession = getSavedSession();
    if (savedSession) {
      setUserProfile(prev => ({
        ...prev,
        name: savedSession.displayName || prev.name,
        email: savedSession.email || prev.email,
        phone: savedSession.phone || prev.phone,
        avatar: savedSession.photoURL || prev.avatar
      }));
    }

    void handleAuthRedirect().then(user => {
      if (user) {
        setCurrentUser(user);
        setShowAuthModal(false);
        showToast(`Welcome back, ${user.displayName}! Signed in via Google.`);
      }
    });

    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      console.log('🔥 FINAL AUTH CHECK', {
        uid: firebaseUser?.uid ?? null,
        email: firebaseUser?.email ?? null,
        isAnonymous: firebaseUser?.isAnonymous ?? null,
        providers: firebaseUser?.providerData?.map(p => p.providerId) ?? [],
        projectId: auth.app.options.projectId,
      });

      if (!firebaseUser) {
        setCurrentUser(null);
        profilePromptCheckedUid.current = null;
        setShowAuthModal(true);
        setAuthReady(true);
        setIsAuthLoading(false);
        return;
      }

      try {
        await refreshSecurityToken(firebaseUser);
      } catch (claimRefreshError) {
        console.error('Could not refresh security claims after authentication:', claimRefreshError);
        showToast('Secure sign-in could not be refreshed. Please check your connection and retry.');
      }

      const savedSession = getSavedSession();
      const providerId = firebaseUser.providerData[0]?.providerId || 'firebase';
      const existingProfile = await getUserProfileFromFirestore(firebaseUser.uid);

      if (existingProfile?.suspension?.active) {
          const now = new Date();
          const until = existingProfile.suspension.suspendedUntil ? new Date(existingProfile.suspension.suspendedUntil) : null;

          if (!until || until > now) {
              setCurrentUser({ ...existingProfile, uid: firebaseUser.uid });
              setAuthReady(true);
              setIsAuthLoading(false);
              return;
          }
      }

      const nextUser: AppUser = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || savedSession?.email || '',
        displayName: firebaseUser.displayName || existingProfile?.displayName || savedSession?.displayName || 'Clue200 Member',
        username: existingProfile?.username || fallbackUsername(firebaseUser.uid, firebaseUser.email),
        photoURL: existingProfile?.profilePhotoURL || firebaseUser.photoURL || existingProfile?.photoURL || savedSession?.photoURL || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&auto=format&fit=crop&q=80',
        profilePhotoURL: existingProfile?.profilePhotoURL,
        role: existingProfile?.role || (firebaseUser.email?.toLowerCase() === 'ajaytripathi821@gmail.com' ? 'superadmin' : undefined),
        loginMethod: savedSession?.uid === firebaseUser.uid ? savedSession.loginMethod : providerId === 'google.com' ? 'google' : 'password',
        createdAt: existingProfile?.createdAt || savedSession?.createdAt || new Date().toISOString(),
        firstJoinedAt: existingProfile?.firstJoinedAt || savedSession?.firstJoinedAt || firebaseUser.metadata.creationTime || new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        profileComplete: existingProfile?.profileComplete === true || savedSession?.profileComplete === true,
        lastProfilePromptAt: existingProfile?.lastProfilePromptAt || savedSession?.lastProfilePromptAt,
        profilePromptSkippedAt: existingProfile?.profilePromptSkippedAt ?? savedSession?.profilePromptSkippedAt,
        phone: existingProfile?.phone || savedSession?.phone,
        verifiedEmail: firebaseUser.emailVerified,
        clueId: existingProfile?.clueId || savedSession?.clueId,
        suspension: existingProfile?.suspension,
        policyConsentVersions: existingProfile?.policyConsentVersions,
        policyConsentUpdatedAt: existingProfile?.policyConsentUpdatedAt
      };

      setCurrentUser(nextUser);
      saveLocalSession(nextUser);
      setUserProfile(prev => ({
        ...prev,
        name: nextUser.displayName || prev.name,
        email: nextUser.email || prev.email,
        phone: nextUser.phone || prev.phone,
        avatar: nextUser.photoURL || prev.avatar
      }));
      setShowAuthModal(false);
      setAuthReady(true);
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authReady || !currentUser) return;
    const updatePresence = () => {
      void setPresence(document.visibilityState === 'visible').catch(error => {
        console.error('Realtime presence unavailable:', error);
      });
    };
    updatePresence();
    document.addEventListener('visibilitychange', updatePresence);
    return () => document.removeEventListener('visibilitychange', updatePresence);
  }, [authReady, currentUser?.uid]);

  // Decide once per authenticated session after Firestore profile data has loaded.
  useEffect(() => {
    if (!currentUser?.uid) {
      profilePromptCheckedUid.current = null;
      setShowProfileForm(false);
      return;
    }
    if (!authReady || isAuthLoading || currentUser.suspension?.active ||
      profilePromptCheckedUid.current === currentUser.uid) return;
    profilePromptCheckedUid.current = currentUser.uid;
    setShowProfileForm(shouldShowProfilePrompt(currentUser));
  }, [authReady, currentUser, isAuthLoading]);

  // Load persistent media history from Firestore (Real-time)
  useEffect(() => {
    if (!currentUser) return;
    const unsub = subscribeToUserMediaHistory(currentUser.uid, (items) => {
        if (items && items.length > 0) {
            setMediaHistory(items);
        }
    });
    return () => unsub();
  }, [currentUser?.uid]);

  // Real-time automatic refresh every 20 minutes (Section: Auto-sync)
  useEffect(() => {
    if (!currentUser) return;

    const REFRESH_INTERVAL = 20 * 60 * 1000; // 20 minutes
    const syncInterval = setInterval(() => {
      console.log('🔄 Automatic 20-min refresh: Syncing profile and stats...');
      void getUserProfileFromFirestore(currentUser.uid).then(profile => {
        if (profile) {
          setCurrentUser(prev => prev ? { ...prev, ...profile } : null);
          // Only update session if data changed to avoid unnecessary saves
          saveLocalSession({ ...currentUser, ...profile });
        }
      });
    }, REFRESH_INTERVAL);

    return () => clearInterval(syncInterval);
  }, [currentUser?.uid]);

  const handleUpdateProfile = (updated: UserProfile) => {
    setUserProfile(updated);
    if (currentUser) {
      const updatedUser: AppUser = {
        ...currentUser,
        displayName: updated.name,
        username: updated.username,
        email: updated.email,
        phone: updated.phone,
        photoURL: updated.avatar,
        profilePhotoURL: updated.avatar
      };
      setCurrentUser(updatedUser);
      saveLocalSession(updatedUser);
    }
    showToast('Profile & settings updated successfully!');
  };

  const handleDeleteHistoryItem = async (id: string) => {
    setMediaHistory(prev => prev.filter(item => item.id !== id));
    showToast('Item deleted from your media history.');
  };

  const handlePublishMedia = async (item: MediaHistoryItem) => {
    setMediaHistory(previous => [item, ...previous.filter(existing => existing.id !== item.id)]);
    showToast(`Published "${item.title}"! It will appear in your history shortly.`);
  };

  const handleAuthSuccess = (user: AppUser) => {
    setCurrentUser(user);
    saveLocalSession(user);
    setUserProfile(prev => ({
      ...prev,
      name: user.displayName,
      email: user.email,
      phone: user.phone || prev.phone,
      avatar: user.photoURL || prev.avatar
    }));
    setShowAuthModal(false);
    showToast(`Welcome back, ${user.displayName}! Session active on clue200.`);
  };

  const handleLogout = async () => {
    await logoutUser();
    setCurrentUser(null);
    setShowAuthModal(true);
    showToast('Logged out successfully.');
  };

  const handleProfileComplete = (updatedUser: AppUser) => {
    setCurrentUser(updatedUser);
    saveLocalSession(updatedUser);
    setUserProfile(previous => ({
      ...previous,
      name: updatedUser.displayName,
      username: updatedUser.username || previous.username,
      email: updatedUser.email,
      phone: updatedUser.phone || updatedUser.phoneNumber || previous.phone,
      avatar: updatedUser.photoURL || previous.avatar
    }));
    setShowProfileForm(false);
    showToast('Profile completed! Welcome to YeTryKar.');
  };

  const handleProfileSkip = async () => {
    if (!currentUser) return;
    try {
      await markProfilePromptSkipped(currentUser.uid);
      const updatedUser: AppUser = {
        ...currentUser,
        profilePromptSkippedAt: Date.now()
      };
      setCurrentUser(updatedUser);
      saveLocalSession(updatedUser);
      setShowProfileForm(false);
      showToast('Profile prompt skipped. We’ll remind you in 30 days.');
    } catch (error) {
      console.error('Could not save the profile prompt skip:', error);
      showToast(error instanceof Error ? error.message : 'Could not save your choice. Please try again.');
    }
  };

  const handlePoliciesAgreed = (versions: Record<string, string>) => {
    setCurrentUser(previous => {
      if (!previous) return previous;
      const updatedUser = { ...previous, policyConsentVersions: versions };
      saveLocalSession(updatedUser);
      return updatedUser;
    });
    localStorage.setItem('yetrykar_policies_agreed', 'true');
    showToast('Policies accepted. You can now use all write features.');
  };

  if (!authReady || isAuthLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="w-full max-w-[430px] min-h-[260px] rounded-3xl bg-white border border-slate-200 shadow-sm p-6 animate-pulse">
          <div className="h-7 w-32 rounded-lg bg-slate-200" />
          <div className="mt-6 h-4 w-2/3 rounded bg-slate-100" />
          <div className="mt-3 h-4 w-1/2 rounded bg-slate-100" />
          <div className="mt-8 grid grid-cols-3 gap-3">
            <div className="h-24 rounded-2xl bg-slate-100" />
            <div className="h-24 rounded-2xl bg-slate-100" />
            <div className="h-24 rounded-2xl bg-slate-100" />
          </div>
        </div>
      </div>
    );
  }

  const isAdminRoute = currentPath === '/admin' || currentPath.startsWith('/admin/');

  if (currentPath === '/delete-account' || currentPath === '/account-deletion' || currentPath.startsWith('/delete-account/') || currentPath.startsWith('/account-deletion/')) {
    return (
      <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading page...</div>}>
        <DeleteAccountPage
          onClose={() => navigateToPath('/')}
        />
      </Suspense>
    );
  }

  if (currentPath === '/updates-for-users' || currentPath.startsWith('/updates-for-users')) {
    return (
      <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading page...</div>}>
        <OfficialDashboard
          onBackToHome={() => navigateToPath('/')}
        />
      </Suspense>
    );
  }

  if (currentPath === '/about') {
    return (
      <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading page...</div>}>
        <AboutPage currentPath={currentPath} onNavigate={navigateToPath} currentUser={currentUser} onOpenAuth={() => setShowAuthModal(true)} />
      </Suspense>
    );
  }
  if (currentPath === '/features') {
    return (
      <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading page...</div>}>
        <FeaturesPage currentPath={currentPath} onNavigate={navigateToPath} currentUser={currentUser} onOpenAuth={() => setShowAuthModal(true)} />
      </Suspense>
    );
  }
  if (currentPath === '/faq') {
    return (
      <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading page...</div>}>
        <FaqPage currentPath={currentPath} onNavigate={navigateToPath} currentUser={currentUser} onOpenAuth={() => setShowAuthModal(true)} />
      </Suspense>
    );
  }
  if (currentPath === '/contact') {
    return (
      <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading page...</div>}>
        <ContactPage currentPath={currentPath} onNavigate={navigateToPath} currentUser={currentUser} onOpenAuth={() => setShowAuthModal(true)} />
      </Suspense>
    );
  }
  if (currentPath === '/privacy-policy') {
    return (
      <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading page...</div>}>
        <PrivacyPage currentPath={currentPath} onNavigate={navigateToPath} currentUser={currentUser} onOpenAuth={() => setShowAuthModal(true)} />
      </Suspense>
    );
  }
  if (currentPath === '/terms') {
    return (
      <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading page...</div>}>
        <TermsPage currentPath={currentPath} onNavigate={navigateToPath} currentUser={currentUser} onOpenAuth={() => setShowAuthModal(true)} />
      </Suspense>
    );
  }
  if (currentPath === '/blog') {
    return (
      <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading page...</div>}>
        <BlogListPage currentPath={currentPath} onNavigate={navigateToPath} currentUser={currentUser} onOpenAuth={() => setShowAuthModal(true)} />
      </Suspense>
    );
  }
  if (currentPath.startsWith('/blog/')) {
    const slug = currentPath.replace('/blog/', '');
    return (
      <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading page...</div>}>
        <BlogPostPage slug={slug} currentPath={currentPath} onNavigate={navigateToPath} currentUser={currentUser} onOpenAuth={() => setShowAuthModal(true)} />
      </Suspense>
    );
  }

  if (currentPath === '/policies' || currentPath.startsWith('/policies/')) {
    return (
      <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading policies...</div>}>
        <PoliciesPage
          currentUser={currentUser}
          onAgreed={handlePoliciesAgreed}
          onClose={() => navigateToPath('/')}
          platform={isNativeApp ? 'android' : 'web'}
        />
      </Suspense>
    );
  }

  const isUserRequestRoute = currentPath === '/feedback-yetrykar' || currentPath === '/app-support';
  if (isUserRequestRoute && currentUser && isPolicyConsentDue(currentUser) && !hasCurrentPolicyConsent(currentUser, policies)) {
    return (
      <PoliciesPage
        currentUser={currentUser}
        forceAgreement
        onAgreed={handlePoliciesAgreed}
        platform={isNativeApp ? 'android' : 'web'}
      />
    );
  }

  if (currentPath === '/feedback-yetrykar') {
    return (
      <>
        <FeedbackPage
          currentUser={currentUser}
          onOpenAuth={() => setShowAuthModal(true)}
          onClose={() => navigateToPath('/')}
        />
        {authReady && !currentUser && (
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={handleAuthSuccess}
            onOpenPolicies={() => navigateToPath('/policies')}
          />
        )}
      </>
    );
  }

  if (currentPath === '/app-support') {
    return (
      <>
        <AppSupportPage
          currentUser={currentUser}
          onOpenAuth={() => setShowAuthModal(true)}
          onClose={() => navigateToPath('/')}
        />
        {authReady && !currentUser && (
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={handleAuthSuccess}
            onOpenPolicies={() => navigateToPath('/policies')}
          />
        )}
      </>
    );
  }

  if (isAdminRoute) {
    if (!authReady || isAuthLoading) {
      return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-700">Loading admin access...</div>;
    }

    if (!currentUser) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
          <div className="max-w-md w-full rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl">
            <p className="text-xs font-black uppercase tracking-[0.3em] text-slate-500">YeTryKar Admin</p>
            <h1 className="mt-4 text-2xl font-black text-slate-900">Sign in to continue</h1>
            <p className="mt-3 text-sm text-slate-600">
              Sign in or create a standard account. Admin access is granted separately by an authorized Super Admin.
            </p>
            <button
              onClick={() => navigateToPath('/')}
              className="mt-6 inline-flex items-center justify-center rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700"
            >
              Return to YeTryKar
            </button>
          </div>
          <AuthModal
            isOpen={authReady}
            onClose={() => {
              if (!auth.currentUser) navigateToPath('/');
            }}
            onAuthSuccess={handleAuthSuccess}
            onOpenPolicies={() => navigateToPath('/policies')}
          />
        </div>
      );
    }

    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-700">Loading admin access...</div>}>
        <AdminRouter />
      </Suspense>
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-zinc-100 text-slate-900 flex flex-col font-sans selection:bg-blue-600 selection:text-white">
      {/* 1. Suspension Shield (Section 2.3) */}
      {currentUser?.suspension?.active && (
         <SuspensionScreen
           suspension={currentUser.suspension}
           onLogout={handleLogout}
           onReadPolicies={() => navigateToPath('/policies')}
         />
      )}

      {/* Native Android uses the mobile shell; browsers use the desktop website shell. */}
      {!isAuthLoading && authReady && (
        isNativeApp ? (
          <MobileApp
            activeTab={activeTab}
            onTabChange={setActiveTab}
            userProfile={userProfile}
            onUpdateProfile={handleUpdateProfile}
            mediaHistory={mediaHistory}
            onDeleteHistoryItem={handleDeleteHistoryItem}
            onPublishMedia={handlePublishMedia}
            currentUser={currentUser}
            onOpenAuth={() => setShowAuthModal(true)}
            onLogout={handleLogout}
            onOpenPolicies={() => navigateToPath('/policies')}
            onOpenFeedback={() => navigateToPath('/feedback-yetrykar')}
            onOpenAppSupport={() => navigateToPath('/app-support')}
          />
        ) : (
          <WebView
            activeTab={activeTab}
            onTabChange={setActiveTab}
            userProfile={userProfile}
            onUpdateProfile={handleUpdateProfile}
            mediaHistory={mediaHistory}
            onDeleteHistoryItem={handleDeleteHistoryItem}
            onPublishMedia={handlePublishMedia}
            currentUser={currentUser}
            onOpenAuth={() => setShowAuthModal(true)}
            onLogout={handleLogout}
            onOpenPolicies={() => navigateToPath('/policies')}
            onOpenFeedback={() => navigateToPath('/feedback-yetrykar')}
            onOpenAppSupport={() => navigateToPath('/app-support')}
          />
        )
      )}

      {/* Auth Modal (Google, Email OTP, Phone OTP, Persistent Login) */}
      {authReady && !currentUser && (
        <Suspense fallback={null}>
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => {
              if (currentUser) setShowAuthModal(false);
            }}
            onAuthSuccess={handleAuthSuccess}
            onOpenPolicies={() => navigateToPath('/policies')}
          />
        </Suspense>
      )}

      {/* Mandatory Profile Form (Section 2) */}
      {showProfileForm && authReady && !isAuthLoading && currentUser && (
        <CompleteProfileForm
            user={currentUser}
            onComplete={handleProfileComplete}
            onSkip={handleProfileSkip}
        />
      )}

      {currentUser && localStorage.getItem('yetrykar_policies_agreed') !== 'true' && !hasCurrentPolicyConsent(currentUser, policies) && (
        <Suspense fallback={null}>
          <PoliciesPage
            currentUser={currentUser}
            forceAgreement
            platform={isNativeApp ? 'android' : 'web'}
            onAgreed={handlePoliciesAgreed}
          />
        </Suspense>
      )}

      {/* App Startup Permissions Modal (Location, Camera, Audio, Video, Image, Both Cameras) */}
      <Suspense fallback={null}>
        <AppPermissionsModal
          isOpen={showPermissionsModal}
          onAgreeAndContinue={() => {
            localStorage.setItem('yetrykar_permissions_agreed', 'true');
            setShowPermissionsModal(false);
          }}
        />
      </Suspense>

      {/* Global Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-16 sm:bottom-6 right-6 z-50 bg-slate-900 text-white text-xs font-medium px-4 py-3 rounded-xl shadow-xl flex items-center gap-2 border border-slate-800 animate-in slide-in-from-bottom-5">
          <Check className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
}
